import * as FRAGS from '@thatopen/fragments';
import { Loader2 } from 'lucide-react';
import { useEffect, useMemo, useState, type FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';
import { useDialog } from '@/providers/DialogProvider';
import { Button } from './ui/button';
import { DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { getFragments } from './IFC2';

interface Props {
  modelId: string;
  localIds: number[];
  title: string;
}

/**
 * Aggregates IFC quantities (Qto_* psets) over a set of localIds.
 *
 * Strategy: batch-fetch each id's IsDefinedBy relations (which carry the
 * psets), filter those whose `Name` starts with `Qto_`, then sum every
 * numeric property by `${PsetName}.${PropertyName}`. Also tallies element
 * count by IFC `_category`.
 *
 * Note: data is only as good as the IFC export — not all authoring tools
 * write Qto_* psets, in which case the panel just shows class counts.
 */
export const QuantityTakeoffDialog: FunctionComponent<Props> = ({ modelId, localIds, title }) => {
  const { t } = useTranslation('components');
  const dialog = useDialog();
  const [loading, setLoading] = useState(true);
  const [classCounts, setClassCounts] = useState<Record<string, number>>({});
  const [qtoSums, setQtoSums] = useState<Record<string, { sum: number; count: number }>>({});
  const [scanned, setScanned] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const fragments = await getFragments();
        const model = fragments.models.list.get(modelId);
        if (!model) {
          setLoading(false);
          return;
        }
        const config: Partial<FRAGS.ItemsDataConfig> = {
          attributesDefault: true,
          relationsDefault: { attributes: false, relations: false },
          relations: { IsDefinedBy: { attributes: true, relations: false } },
        };
        const counts: Record<string, number> = {};
        const sums: Record<string, { sum: number; count: number }> = {};
        const CHUNK = 500;
        for (let i = 0; i < localIds.length && !cancelled; i += CHUNK) {
          const chunk = localIds.slice(i, i + CHUNK);
          const datas = (await model.getItemsData(chunk, config)) ?? [];
          for (const d of datas) {
            if (!d) continue;
            const cat = (d._category as FRAGS.ItemAttribute | undefined)?.value;
            if (typeof cat === 'string') counts[cat] = (counts[cat] ?? 0) + 1;
            const psets = d.IsDefinedBy as FRAGS.ItemData[] | undefined;
            if (!Array.isArray(psets)) continue;
            for (const pset of psets) {
              const psetName = (pset.Name as FRAGS.ItemAttribute | undefined)?.value;
              if (typeof psetName !== 'string' || !psetName.startsWith('Qto_')) continue;
              for (const k of Object.keys(pset)) {
                if (k.startsWith('_') || k === 'Name') continue;
                const v = (pset[k] as FRAGS.ItemAttribute | undefined)?.value;
                if (typeof v === 'number' && Number.isFinite(v)) {
                  const key = `${psetName}.${k}`;
                  if (!sums[key]) sums[key] = { sum: 0, count: 0 };
                  sums[key].sum += v;
                  sums[key].count += 1;
                }
              }
            }
          }
          if (!cancelled) setScanned(Math.min(i + CHUNK, localIds.length));
        }
        if (!cancelled) {
          setClassCounts(counts);
          setQtoSums(sums);
          setLoading(false);
        }
      } catch (err) {
        console.error('[QuantityTakeoff]', err);
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [modelId, localIds]);

  const sortedCounts = useMemo(
    () => Object.entries(classCounts).sort((a, b) => b[1] - a[1]),
    [classCounts],
  );
  const sortedSums = useMemo(
    () => Object.entries(qtoSums).sort((a, b) => a[0].localeCompare(b[0])),
    [qtoSums],
  );

  return (
    <>
      <DialogHeader>
        <DialogTitle>{t('quantityTakeoff.title')}</DialogTitle>
        <DialogDescription className='truncate'>{title}</DialogDescription>
      </DialogHeader>
      <div className='max-h-[60vh] overflow-auto flex flex-col gap-4 py-4'>
        {loading ? (
          <div className='flex items-center gap-2 text-sm text-muted-foreground'>
            <Loader2 className='size-4 animate-spin' />
            {t('quantityTakeoff.loading', { scanned, total: localIds.length })}
          </div>
        ) : (
          <>
            <section className='flex flex-col gap-1'>
              <h3 className='text-xs font-medium text-muted-foreground'>
                {t('quantityTakeoff.elementsByClass')} · {localIds.length}
              </h3>
              <div className='flex flex-col border rounded-md text-xs'>
                {sortedCounts.length === 0 ? (
                  <div className='px-3 py-2 text-muted-foreground'>—</div>
                ) : (
                  sortedCounts.map(([cat, n]) => (
                    <div
                      key={cat}
                      className='flex justify-between border-b last:border-b-0 px-3 py-1.5 font-mono'>
                      <span>{cat}</span>
                      <span>{n}</span>
                    </div>
                  ))
                )}
              </div>
            </section>
            <section className='flex flex-col gap-1'>
              <h3 className='text-xs font-medium text-muted-foreground'>
                {t('quantityTakeoff.quantities')}
              </h3>
              {sortedSums.length > 0 ? (
                <div className='flex flex-col border rounded-md text-xs'>
                  {sortedSums.map(([key, { sum, count }]) => (
                    <div
                      key={key}
                      className='flex justify-between gap-2 border-b last:border-b-0 px-3 py-1.5 font-mono'>
                      <span className='truncate'>{key}</span>
                      <span className='shrink-0'>
                        {sum.toLocaleString(undefined, { maximumFractionDigits: 3 })}
                        <span className='text-muted-foreground'> · n={count}</span>
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className='text-xs text-muted-foreground'>{t('quantityTakeoff.noQto')}</p>
              )}
            </section>
          </>
        )}
      </div>
      <DialogFooter>
        <Button variant='outline' onClick={() => dialog.dismissActiveDialog()}>
          {t('quantityTakeoff.close')}
        </Button>
      </DialogFooter>
    </>
  );
};
